import { gql } from '@apollo/client';
import {
  SURVEILLANCE_ROUTE_FRAGMENT,
  SURVEILLANCE_ROUTE_FULL_FRAGMENT,
  SURVEILLANCE_ROUTE_EDIT_FRAGMENT,
  SURVEILLANCE_ROUTE_REPORT_FRAGMENT,
  SURVEILLANCE_ROUTE_CARD_FRAGMENT,
  ROUTE_SEGMENT_FRAGMENT,
  ROUTE_SEGMENT_DETAIL_FRAGMENT,
} from '../fragments/surveillanceRoute';

// ═══════════════════════════════════════════════════════════════════
// QUERIES - SURVEILLANCE ROUTES
// ═══════════════════════════════════════════════════════════════════

export const GET_SURVEILLANCE_ROUTE_BY_ID = gql`
  ${SURVEILLANCE_ROUTE_FULL_FRAGMENT}
  query GetSurveillanceRouteById(
    $id: ID!
    $withShiftWarning: Boolean! = true
  ) {
    surveillanceRoute(id: $id) {
      ...SurveillanceRouteFullFragment
      # Prevención P4 (2026-10-15): aviso «más de 24 h» del detalle. Fuera del
      # fragment a propósito: solo lo pide esta query (lo calcula el servidor
      # leyendo los tramos; ver la policy de SurveillanceRoute). Por defecto
      # SÍ: el detalle y las pantallas que comparten la query (y deduplican la
      # petición) no pasan nada; el Excel con filas marcadas lo apaga (QA T6).
      shiftWarning @include(if: $withShiftWarning)
    }
  }
`;

// Informe RV (2026-07-27): query propia del informe PDF — fragment sin
// `gpsPoints` y con los vínculos de cada tramo. `operationName` NUEVO
// (`GetSurveillanceRouteForReport`): no se renombra ninguna query existente,
// los mocks E2E dependen del nombre.
export const GET_SURVEILLANCE_ROUTE_FOR_REPORT = gql`
  ${SURVEILLANCE_ROUTE_REPORT_FRAGMENT}
  query GetSurveillanceRouteForReport($id: ID!) {
    surveillanceRoute(id: $id) {
      ...SurveillanceRouteReportFragment
    }
  }
`;

// Perf E3 (2026-07-20): query LIGERA propia del wizard `EditRoute` — NUNCA
// reutilices `GET_SURVEILLANCE_ROUTE_BY_ID` (Full fragment, con
// `gpsPoints(simplified: true)`) para precargar el editar. Ver
// `SURVEILLANCE_ROUTE_EDIT_FRAGMENT` para el razonamiento del recorte
// (54,5kB→~2kB medidos, 51,4kB eran 259 puntos GPS sin uso en el wizard).
export const GET_SURVEILLANCE_ROUTE_FOR_EDIT = gql`
  ${SURVEILLANCE_ROUTE_EDIT_FRAGMENT}
  query GetSurveillanceRouteForEdit($id: ID!) {
    surveillanceRoute(id: $id) {
      ...SurveillanceRouteEditFragment
    }
  }
`;

// ⚠️ Perf detalle (2026-07-21): esta query con el Full fragment (61,4kB/1,9s
// medidos, gpsPoints+segments sin uso) ya NO tiene call-sites en producción —
// los dos consumidores de `gql/selectors/orders.ts` usan la variante ligera
// `GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID` de abajo. Se conserva exportada
// por la regla de oro E2E (no cambiar operationName de queries existentes)
// y por si un flujo futuro necesita realmente el track/tramos por orderId.
export const GET_SURVEILLANCE_ROUTE_BY_ORDER_ID = gql`
  ${SURVEILLANCE_ROUTE_FULL_FRAGMENT}
  query GetSurveillanceRouteByOrderId($orderId: ID!) {
    surveillanceRouteByOrderId(orderId: $orderId) {
      ...SurveillanceRouteFullFragment
    }
  }
`;

// Perf detalle (2026-07-21): variante LIGERA de la de arriba — precedente
// exacto `GET_SURVEILLANCE_ROUTE_FOR_EDIT` (E3). Ver el razonamiento del
// recorte en `SURVEILLANCE_ROUTE_CARD_FRAGMENT`. Mismo root field
// `surveillanceRouteByOrderId` → la caché normalizada se comparte con
// cualquier otra query del mismo field (subset ⊆ Full ⇒ cache-first puede
// dar hit cruzado sin red).
export const GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID = gql`
  ${SURVEILLANCE_ROUTE_CARD_FRAGMENT}
  query GetSurveillanceRouteCardByOrderId($orderId: ID!) {
    surveillanceRouteByOrderId(orderId: $orderId) {
      ...SurveillanceRouteCardFragment
    }
  }
`;

export const GET_SURVEILLANCE_ROUTES = gql`
  ${SURVEILLANCE_ROUTE_FRAGMENT}
  query GetSurveillanceRoutes(
    $input: FilterSurveillanceRouteList!
    $withShiftWarning: Boolean! = false
  ) {
    surveillanceRoutes(input: $input) {
      surveillanceRoutes {
        ...SurveillanceRouteFragment
        # Prevención P3 (2026-10-15): marca del listado. 🔴 Solo la pide el
        # listado ACTIVO (withShiftWarning: true): esta misma query la
        # reutilizan los informes (Excel/PDF con take 99999) y el histórico,
        # que no la enseñan, y cada recorrido cuesta una lectura de tramos.
        shiftWarning @include(if: $withShiftWarning)
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// Transfer básica→RV (2026-07-21): variante "modal" del listado paginado para
// el TransferSubOrderModal en modo RV. MISMO field `surveillanceRoutes` (la
// policy de policies.ts ya lista '@connection' al final de sus keyArgs →
// cajón Apollo propio SIN tocar policies), mismo mecanismo que
// GET_GENERIC_ORDERS_MODAL / GET_CAMPAIGNS_MODAL. A diferencia de las
// variantes modal de abajo (campos sin policy propia, que necesitan
// `filter: ["input"]` — ver el bloque de comentario sobre
// GET_PENDING_EXTERNAL_SEGMENTS_MODAL), aquí las variables del input YA
// particionan la caché vía los keyArgs de la policy: `key: "modal"` basta.
export const GET_SURVEILLANCE_ROUTES_MODAL = gql`
  ${SURVEILLANCE_ROUTE_FRAGMENT}
  query GetSurveillanceRoutesModal($input: FilterSurveillanceRouteList!) {
    surveillanceRoutes(input: $input) @connection(key: "modal") {
      surveillanceRoutes {
        ...SurveillanceRouteFragment
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// ═══════════════════════════════════════════════════════════════════
// QUERIES - ROUTE SEGMENTS
// ═══════════════════════════════════════════════════════════════════

// D3 (2026-07-23): el detalle usa el fragment DETALLE (con los vínculos del
// tramo). El resto de queries de tramos (timer/modales) siguen con el base.
// operationName `GetRouteSegments` sin cambios (mocks E2E dependen de él).
// «Registrar parada» (spec 2026-10-06): paradas que el GPS crudo de un tramo
// cerrado deja ver, con la OT que se ligaría si se registran como aviso.
export const GET_SEGMENT_STOP_SUGGESTIONS = gql`
  query GetSegmentStopSuggestions($segmentId: ID!) {
    segmentStopSuggestions(segmentId: $segmentId) {
      startDate
      endDate
      minutes
      latitude
      longitude
      pkKm
      pkMeter
      roadId
      onRoad
      position
      candidatesCount
      candidateOrder {
        id
        roid
        type
        state
      }
    }
  }
`;

// Transferir tramo (2026-10-07): recorridos candidatos y si el tramo cabe.
export const GET_SEGMENT_TRANSFER_TARGETS = gql`
  query GetSegmentTransferTargets($segmentId: ID!) {
    segmentTransferTargets(segmentId: $segmentId) {
      routeId
      roid
      name
      state
      firstStart
      lastEnd
      segmentsCount
      fits
      reasonCode
      pkWarningCode
      prevEnd
      nextStart
    }
  }
`;

/**
 * «Mover tramos» (plan 2026-10-15 § 3): los recorridos a los que se puede
 * mover un rango de tramos y si el bloque cabe en cada uno (mismas reglas que
 * transferir). Devuelve la misma forma que `segmentTransferTargets`.
 */
export const GET_ROUTE_MOVE_TARGETS = gql`
  query GetRouteMoveTargets($sourceRouteId: ID!, $segmentIds: [ID!]!) {
    routeMoveTargets(sourceRouteId: $sourceRouteId, segmentIds: $segmentIds) {
      routeId
      roid
      name
      state
      firstStart
      lastEnd
      segmentsCount
      fits
      reasonCode
      pkWarningCode
      prevEnd
      nextStart
    }
  }
`;

/**
 * Revisión del 22-oct: las tablas de destinos de «Transferir tramo» y «Mover
 * tramos» con los filtros, el orden y las páginas del listado de recorridos
 * (`FilterSurveillanceRouteList`). Equipo, estado y «no es el propio
 * recorrido» los fuerza el servidor.
 */
const TRANSFER_TARGET_PAGE_FIELDS = `
  totalCount
  targets {
    routeId
    roid
    name
    state
    firstStart
    lastEnd
    segmentsCount
    fits
    reasonCode
    pkWarningCode
    prevEnd
    nextStart
  }
`;

export const GET_SEGMENT_TRANSFER_TARGET_PAGE = gql`
  query GetSegmentTransferTargetPage(
    $segmentId: ID!
    $filter: FilterSurveillanceRouteList
    $skip: Int
    $take: Int
  ) {
    segmentTransferTargetPage(
      segmentId: $segmentId
      filter: $filter
      skip: $skip
      take: $take
    ) {
      ${TRANSFER_TARGET_PAGE_FIELDS}
    }
  }
`;

export const GET_ROUTE_MOVE_TARGET_PAGE = gql`
  query GetRouteMoveTargetPage(
    $sourceRouteId: ID!
    $segmentIds: [ID!]!
    $filter: FilterSurveillanceRouteList
    $skip: Int
    $take: Int
  ) {
    routeMoveTargetPage(
      sourceRouteId: $sourceRouteId
      segmentIds: $segmentIds
      filter: $filter
      skip: $skip
      take: $take
    ) {
      ${TRANSFER_TARGET_PAGE_FIELDS}
    }
  }
`;

/**
 * «Mover tramos»: los tramos del recorrido destino, para «Así quedará». Con
 * nombre de operación propio: la página ya pide `GetRouteSegments` para el
 * origen y una misma operación con dos recorridos se pisaría en los mocks.
 */
export const GET_MOVE_TARGET_SEGMENTS = gql`
  ${ROUTE_SEGMENT_DETAIL_FRAGMENT}
  query GetMoveTargetSegments($surveillanceRouteId: ID!) {
    routeSegments(surveillanceRouteId: $surveillanceRouteId) {
      ...RouteSegmentDetailFragment
    }
  }
`;

export const GET_ROUTE_SEGMENTS = gql`
  ${ROUTE_SEGMENT_DETAIL_FRAGMENT}
  query GetRouteSegments($surveillanceRouteId: ID!) {
    routeSegments(surveillanceRouteId: $surveillanceRouteId) {
      ...RouteSegmentDetailFragment
    }
  }
`;

export const GET_ROUTE_SEGMENT = gql`
  ${ROUTE_SEGMENT_FRAGMENT}
  query GetRouteSegment($id: ID!) {
    routeSegment(id: $id) {
      ...RouteSegmentFragment
      # De dónde vino (revisión del 22-oct): la card de «Órdenes asociadas»
      # lo enseña como las actuaciones transferidas.
      transfer {
        fromRouteId
        fromRouteRoid
        fromRoid
        at
        byUserId
      }
    }
  }
`;

// V4 D3 (2026-04-22): RVs operativos para el TimerOverlay. Filtra por concesión
// y equipo si se pasa el input (usa el patrón autoseleccionable de P10 FR-10.2).
export const GET_ACTIVE_SURVEILLANCE_ROUTES = gql`
  ${SURVEILLANCE_ROUTE_FRAGMENT}
  query GetActiveSurveillanceRoutes(
    $input: FilterActiveSurveillanceRoutes
    $isTablet: Boolean! = false
  ) {
    activeSurveillanceRoutes(input: $input) {
      ...SurveillanceRouteFragment
      # Prevención P1 (2026-10-15): el cronómetro pregunta antes de seguir un
      # turno viejo. Viaja en el sondeo para quedar en caché y servir sin red.
      # Solo para la tablet (isTablet): el cronómetro se monta para todos
      # los roles y los demás no lo usan.
      lastSegmentEndDate @include(if: $isTablet)
      # QA T6b (2026-10-15): cuentas con un tramo ABIERTO. «Cronómetro activo
      # en otro recorrido» es eso (assertNoOtherActiveTimer), no el estado
      # in-progress: un recorrido en curso sin tramos no bloquea.
      openSegmentUserIds @include(if: $isTablet)
    }
  }
`;

// V4 D3 (2026-04-22): tramo activo de un RV (endDate=null). Devuelve null si
// el RV no tiene tramo corriendo. Usado por TimerOverlay para decidir qué
// cronómetro + botones mostrar.
export const GET_ACTIVE_ROUTE_SEGMENT = gql`
  ${ROUTE_SEGMENT_FRAGMENT}
  query GetActiveRouteSegment($surveillanceRouteId: ID!) {
    activeRouteSegment(surveillanceRouteId: $surveillanceRouteId) {
      ...RouteSegmentFragment
    }
  }
`;

// V4 P15 (2026-04-22): tramos elegibles para vincular desde sala / desde detail
// de actuació. Por defecto filtra `external-call` + `pendingExternalLink=true`,
// pero el usuario puede ampliar tipos en el modal (FR-15.2). Consumido por
// `RelateSegmentModal`.
export const GET_PENDING_EXTERNAL_SEGMENTS = gql`
  ${ROUTE_SEGMENT_FRAGMENT}
  query GetPendingExternalSegments($input: FilterPendingExternalSegments) {
    pendingExternalSegments(input: $input) {
      ...RouteSegmentFragment
    }
  }
`;

// V4 PR6 (2026-05-07): variantes "modal" con `@connection(key: "modal")` para
// aislar el cache de Apollo cuando la query se consume desde un modal. Mismo
// patrón que `GET_GENERIC_ORDERS_MODAL` / `GET_CAMPAIGNS_MODAL` post-`55e1eea`
// de main. Permite que el modal se abra+cierre+reabra con su propio estado de
// cache sin contaminar otros consumidores de la misma query (ej. SegmentList
// del detail RV o el dropdown del TimerOverlay).
//
// Cuando el backend añada paginación cursor a estas queries (deuda técnica
// PR 6 backend — schema todavía expone arrays planos sin `pageInfo`), aquí
// solo habrá que añadir `take`/`cursor` al input y `@connection(key: "modal")`
// seguirá garantizando el aislamiento.
// `filter: ["input"]` (2026-07-10, cazado por el usuario): sin él,
// `@connection(key: "modal")` crea UN SOLO cajón de caché IGNORANDO las
// variables — el modo actuación (pauseCauses:[action] + RV) y el modo OT
// externa (external-call, sin RV) compartían cajón, y con cache-and-network
// el modal pintaba primero los tramos del OTRO modo (flash de filas ajenas
// que luego "desaparecían"). Con filter, cada input tiene su cajón — el
// aislamiento por modo que las modales canónicas logran usando CAMPOS
// distintos (genericOrders vs campaigns).
export const GET_PENDING_EXTERNAL_SEGMENTS_MODAL = gql`
  ${ROUTE_SEGMENT_FRAGMENT}
  query GetPendingExternalSegmentsModal($input: FilterPendingExternalSegments) {
    pendingExternalSegments(input: $input)
      @connection(key: "modal", filter: ["input"]) {
      ...RouteSegmentFragment
      # Columna Carretera del modal (2026-09-23): un tramo de un recorrido de
      # UNA via guarda roadId nulo, y su via es la primera del recorrido. Solo
      # aqui y no en el fragmento base: el cronometro lo sondea cada 30 s y no
      # lo necesita. Requiere el backend con SurveillanceRouteRef.roadIDs.
      surveillanceRoute {
        id
        roadIDs
      }
    }
  }
`;

export const GET_ACTIVE_SURVEILLANCE_ROUTES_MODAL = gql`
  ${SURVEILLANCE_ROUTE_FRAGMENT}
  query GetActiveSurveillanceRoutesModal(
    $input: FilterActiveSurveillanceRoutes
  ) {
    activeSurveillanceRoutes(input: $input) @connection(key: "modal") {
      ...SurveillanceRouteFragment
    }
  }
`;

// Actuación de cada aviso a sala (spec 2026-10-27 § 6): consulta APARTE, fuera
// de los fragments, que se pide siempre con `no-cache` — este dato se calcula
// al leer y no se guarda para leerlo sin red.
export const GET_ROUTE_SEGMENT_EXTERNAL_WORKS = gql`
  query GetRouteSegmentExternalWorks($id: ID!) {
    routeSegments(surveillanceRouteId: $id) {
      id
      linkedExternalWorks {
        id
        roid
        type
        state
        actionId
        accidentId
      }
    }
  }
`;
